import React, { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingUp, Layers, Activity, BarChart2, Eye } from "lucide-react";

const COIN_COLORS = {
  btc: "#f7931a",
  eth: "#627eea",
  sol: "#14f195",
  bnb: "#f3ba2f",
  xrp: "#00aed1",
  ada: "#3cc8ff",
  doge: "#cb9800",
  avax: "#e84142",
  dot: "#e6007a",
  link: "#375bd2",
  shib: "#ff8a00",
  near: "#000000",
};

const GlobalChart = ({ coinsData, onOpenModal }) => {
  const [chartMode, setChartMode] = useState("overlay"); // 'overlay' | 'heatstream'
  const [timeframe, setTimeframe] = useState("7d"); // '24h' | '7d'
  const [selectedCoins, setSelectedCoins] = useState([
    "bitcoin",
    "ethereum",
    "solana",
    "binancecoin",
    "ripple",
  ]);

  const excludeCoin = (coinSymbol) => {
    const stables = ["usdt", "usdc", "busd", "dai", "ust", "mim", "tusd", "usdd", "usdp", "pyusd"];
    return !stables.includes(coinSymbol.toLowerCase());
  };

  const topCoins = useMemo(() => {
    return (coinsData || [])
      .filter((c) => excludeCoin(c.symbol) && c.market_cap > 0)
      .slice(0, 10);
  }, [coinsData]);

  // Generate normalized comparative time-series data for the multi-line overlay chart
  const lineChartData = useMemo(() => {
    if (!topCoins || topCoins.length === 0) return [];

    const numPoints = 14;
    const dataPoints = [];

    for (let i = 0; i < numPoints; i++) {
      const point = {
        time: timeframe === "24h" ? `${i * 2}h` : `Day ${i + 1}`,
      };

      topCoins.forEach((coin) => {
        const change24h = coin.price_change_percentage_24h_in_currency ?? coin.market_cap_change_percentage_24h ?? 0;
        const change7d = coin.price_change_percentage_7d_in_currency ?? change24h * 2;
        const targetChange = timeframe === "24h" ? change24h : change7d;

        // Simulate normalized progress curve from 0% to targetChange %
        const progressFactor = i / (numPoints - 1);
        const noise = (Math.sin(i + coin.symbol.length) * (Math.abs(targetChange) * 0.15));
        const val = Number((targetChange * progressFactor + noise).toFixed(2));
        point[coin.id] = val;
      });

      dataPoints.push(point);
    }
    return dataPoints;
  }, [topCoins, timeframe]);

  const toggleCoinSelection = (coinId) => {
    if (selectedCoins.includes(coinId)) {
      if (selectedCoins.length > 1) {
        setSelectedCoins(selectedCoins.filter((id) => id !== coinId));
      }
    } else {
      setSelectedCoins([...selectedCoins, coinId]);
    }
  };

  const formatPrice = (num) => {
    if (!num) return "-";
    if (num < 0.01) return `$${num.toFixed(6)}`;
    if (num < 1) return `$${num.toFixed(4)}`;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatCompactUSD = (num) => {
    if (!num) return "-";
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  const totalMarketCap = topCoins.reduce((sum, c) => sum + (c.market_cap || 0), 0);

  const CustomOverlayTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-overlay-tooltip">
          <p className="tooltip-time-label">{label}</p>
          <div className="tooltip-lines-list">
            {payload.map((item) => {
              const coinObj = topCoins.find((c) => c.id === item.dataKey);
              const val = item.value;
              const isGain = val >= 0;
              return (
                <div key={item.dataKey} className="tooltip-line-row">
                  <span
                    className="dot-indicator"
                    style={{ background: item.color }}
                  ></span>
                  <span className="coin-name">{coinObj ? coinObj.name : item.dataKey}:</span>
                  <strong className={isGain ? "gain-text" : "loss-text"}>
                    {isGain ? "+" : ""}
                    {val}%
                  </strong>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="aggregated-chart-wrapper">
      {/* Header Bar */}
      <div className="aggregated-header">
        <div className="title-group">
          <div className="title-glow-badge">
            <Activity size={20} className="activity-icon" />
          </div>
          <div>
            <h3>Aggregated Multi-Currency Chart</h3>
            <span className="title-subtitle">Single Canvas Comparative Market Dynamics</span>
          </div>
        </div>

        <div className="action-controls">
          {/* Mode Selector */}
          <div className="mode-toggle-group">
            <button
              className={`mode-btn ${chartMode === "overlay" ? "active" : ""}`}
              onClick={() => setChartMode("overlay")}
            >
              <TrendingUp size={15} />
              <span>Comparative Overlay</span>
            </button>
            <button
              className={`mode-btn ${chartMode === "heatstream" ? "active" : ""}`}
              onClick={() => setChartMode("heatstream")}
            >
              <BarChart2 size={15} />
              <span>Market Heat Stream</span>
            </button>
          </div>

          {/* Timeframe Selector */}
          {chartMode === "overlay" && (
            <div className="timeframe-group">
              <button
                className={`time-pill ${timeframe === "24h" ? "active" : ""}`}
                onClick={() => setTimeframe("24h")}
              >
                24H
              </button>
              <button
                className={`time-pill ${timeframe === "7d" ? "active" : ""}`}
                onClick={() => setTimeframe("7d")}
              >
                7D
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Currency Line Toggle Chips */}
      {chartMode === "overlay" && (
        <div className="coin-selector-bar">
          <span className="selector-label">
            <Eye size={13} /> Visible Currency Lines:
          </span>
          <div className="chips-list">
            {topCoins.map((coin) => {
              const isSelected = selectedCoins.includes(coin.id);
              const color = COIN_COLORS[coin.symbol.toLowerCase()] || "#6366f1";

              return (
                <button
                  key={coin.id}
                  className={`coin-chip ${isSelected ? "selected" : ""}`}
                  style={{
                    borderColor: isSelected ? color : "rgba(255,255,255,0.08)",
                    color: isSelected ? "#ffffff" : "#94a3b8",
                  }}
                  onClick={() => toggleCoinSelection(coin.id)}
                >
                  <span
                    className="color-dot"
                    style={{ background: color, opacity: isSelected ? 1 : 0.4 }}
                  ></span>
                  <span>{coin.name}</span>
                  <span className="chip-ticker">({coin.symbol.toUpperCase()})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Aggregated Chart Canvas */}
      <div className="chart-canvas-box">
        {topCoins.length === 0 ? (
          <div className="chart-loading">Loading Aggregated Multi-Currency Data...</div>
        ) : chartMode === "overlay" ? (
          /* Single Multi-Line Comparative Recharts Canvas */
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={lineChartData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis
                stroke="#64748b"
                fontSize={11}
                tickLine={false}
                tickFormatter={(val) => `${val >= 0 ? "+" : ""}${val}%`}
              />
              <Tooltip content={<CustomOverlayTooltip />} />

              {topCoins.map((coin) => {
                if (!selectedCoins.includes(coin.id)) return null;
                const color = COIN_COLORS[coin.symbol.toLowerCase()] || "#6366f1";

                return (
                  <Line
                    key={coin.id}
                    type="monotone"
                    dataKey={coin.id}
                    name={coin.name}
                    stroke={color}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 6, stroke: "#0f1322", strokeWidth: 2 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          /* Stacked Multi-Row Market Heat Stream */
          <div className="heatstream-list">
            {topCoins.map((coin) => {
              const change =
                coin.price_change_percentage_24h_in_currency ??
                coin.market_cap_change_percentage_24h ??
                0;
              const isPositive = change >= 0;
              const sharePct = totalMarketCap > 0 ? ((coin.market_cap / totalMarketCap) * 100).toFixed(1) : 0;
              const lineColor = COIN_COLORS[coin.symbol.toLowerCase()] || "#6366f1";

              return (
                <div
                  key={coin.id}
                  className="heatstream-row-line"
                  onClick={() => onOpenModal && onOpenModal(coin)}
                >
                  <div className="line-coin-label">
                    <span className="line-indicator-dot" style={{ background: lineColor }}></span>
                    <img src={coin.image} alt={coin.name} className="coin-avatar" />
                    <span className="coin-title">{coin.name}</span>
                    <span className="coin-symbol">({coin.symbol.toUpperCase()})</span>
                  </div>

                  <div className="line-heat-track">
                    <div
                      className={`line-heat-fill ${isPositive ? "gain" : "loss"}`}
                      style={{ width: `${Math.max(sharePct * 1.8, 5)}%` }}
                    >
                      <span className="share-text">{sharePct}% Market Share</span>
                    </div>
                  </div>

                  <div className="line-metrics">
                    <span className="line-price">{formatPrice(coin.current_price)}</span>
                    <div className={`line-change-pill ${isPositive ? "gain" : "loss"}`}>
                      {isPositive ? "+" : ""}
                      {change.toFixed(1)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalChart;
